import {
    ActivateDirectBuyOnOrgResponse,
    GetSignupAccountOrgDetailsResponseDto
} from "@/libraries/bxp-web-stargate-client/src";

export interface ProfileMenuUserData {
    avatar: string;
    email: string;
    name: string;
}
export interface DirectBuyOrgSetupProps {
    /**
     * A `testId` prop is provided for specified elements, which is a unique
     * string that appears as a data attribute `data-testid` in the rendered code,
     * serving as a hook for automated tests */
    readonly testId?: string;

    readonly imageAssets: {
        readonly background: string;
        readonly browser: string;
    };

    /**
     * Is user logged in
     */
    readonly isUserLoggedIn?: boolean;

    /**
     * sites available for dropdown
     */
    readonly orgs: GetSignupAccountOrgDetailsResponseDto[];

    /**
     * Spinner Loader
     */
    readonly isLoading: boolean;

    /**
     * Last Item Spinner Loader
     */
    readonly isLastItemLoading: boolean;

    /**
     * Order id
     */
    readonly orderId: string;

    /**
     * Transaction account
     */
    readonly transactionAccount: string;

    /**
     * Flag indicating whether or not to show the error page.
     */
    readonly error?: boolean;

    /**
     * Set status to submitting before redirect to confirmation page.
     */
    readonly isSubmitting?: boolean;

    /**
     * Org Based Product on Org Select page
     */
    readonly orgBasedProduct?: string;

    /**
     * Offering Ids from the order to check org enablement
     */
    readonly offeringIds?: string[];

    /**
     * Trace ID to visualize for debugging
     */
    readonly traceId?: string;

    readonly activateForExistingOrg: (data: {
        orgId: string;
    }) => Promise<unknown>;

    readonly getNextPageOrgs: () => Promise<unknown>;

    readonly activationError?: unknown;
}

export interface ProvisionOnOrgExistingSiteActivationData {
    orderId: string;
}

export interface ProvisionOnOrgNewSiteActivationData {
    siteName: string;
}

export enum DirectBuyStatus {
    ORDER_CONFIRMED = "orderConfirmed",
    POST_PROVISIONING = "postProvisioning"
}

import type { SiteNameAvailabilityStatus } from "./forms";

export interface DirectBuySiteSetupProps {
    /**
     * A `testId` prop is provided for specified elements, which is a unique
     * string that appears as a data attribute `data-testid` in the rendered code,
     * serving as a hook for automated tests */
    readonly testId?: string;
    /**
     * Is user logged in
     */
    readonly isUserLoggedIn?: boolean;

    readonly imageAssets: {
        readonly background: string;
        readonly browser: string;
        readonly error: string;
        readonly serviceSuccess: string;
        readonly urlInBrowser: string;
    };

    readonly isLoading: boolean;

    /**
     * sites available for dropdown
     */
    readonly sites: Site[];

    /**
     * Flag to indicate locked in site
     */
    readonly siteFlags?: SiteFlags;

    /**
     * Order id
     */
    readonly orderId: string;

    /**
     * Transaction account
     */
    readonly transactionAccount: string;

    /**
     * An async function that checks whether the site name is taken
     */
    readonly getSiteNameAvailability: (
        siteName: string
    ) => Promise<SiteNameAvailabilityStatus>;

    /**
     * Flag indicating whether or not to show the error page.
     */
    readonly error?: boolean;

    readonly activateForExistingSite: (
        data: DirectBuyExistingSiteActivationData
    ) => Promise<unknown>;

    readonly activateForNewSite: (
        data: DirectBuyNewSiteActivationData
    ) => Promise<unknown>;

    readonly activateForExistingOrg: (data: {
        orgId: string;
        orderId: string;
        transactionAccountId: string;
    }) => Promise<ActivateDirectBuyOnOrgResponse>;

    readonly pollDirectBuyCloudStatus: (
        provisioningRequestId: string
    ) => Promise<boolean>;

    readonly activationError?: unknown;

    readonly splunkUrl?: string;

    readonly activeEntitlements?: {
        /**
         * Flag indicating whether or not to show the active entitlements page.
         */
        readonly showActiveEntitlements: boolean;

        /**
         * How long should the entitlements page show before redirect. Number is in ms.
         */
        readonly activeEntitlementsRedirectTimer: number;

        /**
         * Active entitlements redirect callback.
         */
        readonly activeEntitlementsRedirect: () => void;
    };

    /**
     * Indicates the status of the direct buy journey:
     * - `orderConfirmed`: Represents a continuous journey where the order has been confirmed.
     * - `postProvisioning`: Indicates the journey has been resumed after a user dropped off.
     * This value is used to determine the appropriate site setup header and description displayed to the user.
     */
    readonly directBuyStatus?: DirectBuyStatus;

    /**
     * email of the logged in user
     */
    readonly email?: string;

    /**
     * Trace ID to be visualized for debugging
     */
    readonly traceId?: string;

    /**
     * Provisionable products with their tiers
     */
    readonly provisionableProducts?: Array<{
        readonly productName: string;
        readonly tier?: "Free" | "Standard" | "Premium";
        readonly includedProductKeys?: string[];
    }>;

    /**
     * Preselected org site ids
     */
    readonly preselectedOrgSiteIds?: string[];

    /**
     * Org name
     */
    readonly orgName?: string;
}

export type Site = {
    readonly siteName: string;
    readonly url: string;
    readonly cloudId?: string;
    readonly displayName?: string;
    readonly orderContext?: {
        readonly slug?: string;
    };
};

export type SiteFlags = {
    readonly useLockedInSite: boolean;
    readonly lockedInSiteName?: string;
    readonly provisionableSiteOfferings?: {
        offeringId: string;
        productKey?: string;
        licenseDataSource?: string;
        edition?: string;
    }[];
    readonly productConflictingSites?: Site[];
};

export interface DirectBuyExistingSiteActivationData {
    cloudId: string;
}

export interface DirectBuyNewSiteActivationData {
    siteName: string;
}

export type StatusDetails = {
    title: string;
    description: string;
};

export type DirectBuyHeaderStatusMap = Record<DirectBuyStatus, StatusDetails>;
